import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isAppHost, isMarketingHost, splitActive, marketingUrl, appUrl } from "../config/origins";

/**
 * HostGuard — enforces the marketing/app domain split at runtime.
 *
 * One SPA build serves both linklane.io (marketing) and app.linklane.io (app).
 *
 * On the APP host (app.linklane.io):
 *   • marketing/content path → bounce to the marketing origin (linklane.io)
 *   • root "/"               → /login (or /dashboard if already signed in)
 *   • app + auth + functional → render normally
 *
 * On the MARKETING host (linklane.io):
 *   • app or auth path → bounce to the app origin (app.linklane.io)
 *     so https://linklane.io/dashboard → https://app.linklane.io/dashboard, etc.
 *   • everything else → render normally
 *
 * Inert on localhost/previews (split not active) → dev unchanged. No redirect
 * loops: redirects change origin (app↔marketing) or path (/ → /login), never
 * the same same-origin path.
 */

// Marketing/content surfaces that belong on the marketing origin only.
const MARKETING_PREFIXES = [
  "/pricing",
  "/blog",
  "/guides",
  "/compare",
  "/alternatives",
  "/use-cases",
  "/security",
  "/subprocessors",
  "/privacy",
  "/terms",
  "/about",
  "/contact",
  "/customers",
  "/careers",
  "/developers",
  "/status",
  "/what-is-linklane",
];

// Authenticated app + auth surfaces that belong on the app origin only.
// Note: "/bio/edit" (app) is listed explicitly so it doesn't catch the public
// "/bio/:slug" pages, which stay on the marketing origin.
const APP_PREFIXES = [
  "/dashboard",
  "/links",
  "/analytics",
  "/settings",
  "/campaigns",
  "/bulk",
  "/domains",
  "/api-keys",
  "/webhooks",
  "/team",
  "/integrations",
  "/billing",
  "/bio/edit",
  "/onboarding",
  "/login",
  "/signup",
  "/forgot",
  "/reset-password",
];

function matchesAny(path, prefixes) {
  return prefixes.some((p) => path === p || path.startsWith(`${p}/`));
}

export default function HostGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!splitActive()) return; // localhost / previews → single origin, no guard
    const path = location.pathname;

    if (isAppHost()) {
      // Marketing/content pages don't belong on the app host → send to marketing.
      if (matchesAny(path, MARKETING_PREFIXES)) {
        window.location.replace(marketingUrl(path + location.search));
        return;
      }
      // Bare root on the app host → the app entry point, not the landing page.
      if (path === "/") {
        if (loading) return; // wait for the session check to avoid a wrong bounce
        navigate(user ? "/dashboard" : "/login", { replace: true });
      }
      return;
    }

    if (isMarketingHost()) {
      // App/auth pages don't belong on the marketing host → send to the app.
      if (matchesAny(path, APP_PREFIXES)) {
        window.location.replace(appUrl(path + location.search));
      }
    }
  }, [location, user, loading, navigate]);

  return null;
}
